import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatDateTime } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Search,
  Filter,
  Calendar,
  CreditCard,
  Package,
  FileText,
  Loader2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import PageHeader from '@/components/common/PageHeader';
import { DatePicker } from '@/components/ui/date-picker';

interface Order {
  id: string;
  subscription_id: string;
  subscription_item_id?: string;
  subscription_plan_id?: string;
  operation_type: string;
  quantity_change?: number;
  operation_date: string;
  created_at: string;
  notes?: string;
  subscription?: {
    id: string;
    customer_relationship?: {
      customer?: {
        name: string;
      };
    };
  };
  subscription_items?: {
    id: string;
    subscription_plan?: {
      id: string;
      name: string;
    };
  };
  subscription_plans?: {
    id: string;
    name: string;
  };
}

const SubscriptionsOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [operationTypeFilter, setOperationTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    fetchOrders();
  }, [operationTypeFilter, startDate, endDate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Construir a consulta base
      let query = supabase
        .from('orders')
        .select(`
          id,
          subscription_id,
          subscription_item_id,
          subscription_plan_id,
          operation_type,
          quantity_change,
          operation_date,
          created_at,
          notes,
          subscription:subscription_id (
            id,
            customer_relationship:customer_relationship_id (
              id,
              customer:customer_id (
                id,
                name
              )
            )
          ),
          subscription_items:subscription_item_id (
            id,
            subscription_plan:subscription_plan_id (
              id,
              name
            )
          ),
          subscription_plans:subscription_plan_id (
            id,
            name
          )
        `)
        .order('operation_date', { ascending: false });

      // Aplicar filtro por tipo de operação
      if (operationTypeFilter !== 'all') {
        query = query.eq('operation_type', operationTypeFilter);
      }

      // Aplicar filtro por data de início
      if (startDate) {
        const formattedStartDate = startDate.toISOString();
        query = query.gte('operation_date', formattedStartDate);
      }

      // Aplicar filtro por data de fim
      if (endDate) {
        // Ajustar para o final do dia
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        const formattedEndDate = endOfDay.toISOString();
        query = query.lte('operation_date', formattedEndDate);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Processar os dados
      let ordersList = data || [];
      
      // Aplicar filtro de pesquisa (cliente, assinatura, plano, notas)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        ordersList = ordersList.filter(order => {
          const customerName = order.subscription?.customer_relationship?.customer?.name || '';
          const subscriptionId = order.subscription_id || '';
          const planName = 
            order.subscription_items?.subscription_plan?.name || 
            order.subscription_plans?.name || '';
          const notes = order.notes || '';
          
          return (
            customerName.toLowerCase().includes(searchLower) ||
            subscriptionId.toLowerCase().includes(searchLower) ||
            planName.toLowerCase().includes(searchLower) ||
            notes.toLowerCase().includes(searchLower)
          );
        });
      }
      
      setOrders(ordersList);
    } catch (error) {
      console.error('Erro ao buscar operações:', error);
      toast.error('Não foi possível carregar as operações');
    } finally {
      setLoading(false);
    }
  };

  const getOperationTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'criação': 'Criação',
      'renovação': 'Renovação',
      'cancelamento': 'Cancelamento',
      'aumento': 'Aumento de Quantidade',
      'redução': 'Redução de Quantidade',
      'PRICE_ADJUSTMENT': 'Ajuste de Preço',
      'pagamento': 'Pagamento',
      'cancelamento_fatura': 'Cancelamento de Fatura',
      'creation': 'Criação',
      'renewal': 'Renovação',
      'cancellation': 'Cancelamento',
      'increase': 'Aumento de Quantidade',
      'decrease': 'Redução de Quantidade',
      'payment': 'Pagamento',
      'invoice_cancellation': 'Cancelamento de Fatura',
      'price_adjustment': 'Ajuste de Preço'
    };
    
    return typeMap[type] || type;
  };

  const getOperationTypeBadge = (type: string) => {
    let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'default';
    
    if (['criação', 'renovação', 'aumento', 'pagamento', 'PRICE_ADJUSTMENT'].includes(type)) {
      variant = 'default';
    } else if (['redução'].includes(type)) {
      variant = 'secondary';
    } else if (['cancelamento', 'cancelamento_fatura'].includes(type)) {
      variant = 'destructive';
    } else {
      variant = 'outline';
    }
    
    return <Badge variant={variant}>{getOperationTypeLabel(type)}</Badge>;
  };

  const handleSearch = () => {
    fetchOrders();
  };

  const clearFilters = () => {
    setOperationTypeFilter('all');
    setSearchTerm('');
    setStartDate(undefined);
    setEndDate(undefined);
    fetchOrders();
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Operações de Assinaturas"
        description="Histórico de todas as operações realizadas nas assinaturas"
        breadcrumbItems={[
          { label: "Assinaturas", href: "/subscriptions" },
          { label: "Operações de Assinaturas" }
        ]}
      >
        <Button onClick={fetchOrders} variant="outline" className="gap-1">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </PageHeader>

      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro de pesquisa */}
            <div className="space-y-2">
              <label className="text-sm font-extrabold">Buscar</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  placeholder="Cliente, assinatura, plano..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Filtro por tipo de operação */}
            <div className="space-y-2">
              <label className="text-sm font-extrabold">Tipo de Operação</label>
              <Select value={operationTypeFilter} onValueChange={setOperationTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="criação">Criação</SelectItem>
                  <SelectItem value="renovação">Renovação</SelectItem>
                  <SelectItem value="cancelamento">Cancelamento</SelectItem>
                  <SelectItem value="aumento">Aumento de Quantidade</SelectItem>
                  <SelectItem value="redução">Redução de Quantidade</SelectItem>
                  <SelectItem value="PRICE_ADJUSTMENT">Ajuste de Preço</SelectItem>
                  <SelectItem value="pagamento">Pagamento</SelectItem>
                  <SelectItem value="cancelamento_fatura">Cancelamento de Fatura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtro por data de início */}
            <div className="space-y-2">
              <label className="text-sm font-extrabold">Data Inicial</label>
              <DatePicker
                date={startDate}
                setDate={setStartDate}
                placeholder="Selecione a data inicial"
              />
            </div>
            
            {/* Filtro por data de fim */}
            <div className="space-y-2">
              <label className="text-sm font-extrabold">Data Final</label>
              <DatePicker
                date={endDate}
                setDate={setEndDate}
                placeholder="Selecione a data final"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={clearFilters}>
              Limpar Filtros
            </Button>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Operações */}
      <Card>
        <CardHeader>
          <CardTitle>Operações</CardTitle>
          <CardDescription>
            Lista de todas as operações registradas nas assinaturas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Operação</TableHead>
                <TableHead>Cliente / Plano</TableHead>
                <TableHead>Alteração</TableHead>
                <TableHead>Observações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Carregando operações...
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Nenhuma operação encontrada
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{formatDateTime(order.operation_date)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-xs text-muted-foreground"
                            onClick={() => navigate(`/subscriptions/${order.subscription_id}`)}
                          >
                            {order.subscription_id.substring(0, 8)}...
                          </Button>
                        </span>
                        <span className="font-extrabold text-sm">{getOperationTypeLabel(order.operation_type)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-extrabold">
                          {order.subscription?.customer_relationship?.customer?.name || '-'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {order.subscription_items?.subscription_plan?.name || 
                           order.subscription_plans?.name || '-'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {order.quantity_change !== null && order.quantity_change !== undefined
                        ? (order.quantity_change > 0 ? '+' : '') + order.quantity_change
                        : '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {order.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default SubscriptionsOrders; 